/**
 * Regroupement des serveurs de la page Server (Supervision) : fonctions pures, sans I/O ni store.
 *
 * Famille d'un serveur = son nom sans le suffixe de groupe de jeu (earth-iris -> earth, d'après le
 * catalogue des groupes du projet, jamais une liste en dur) ni son numéro (lobby-2 -> lobby).
 *
 * Pour garder la page courte :
 *   - une famille de 2 serveurs ou plus garde sa section (Lobby, Faction) ;
 *   - un serveur seul de sa famille rejoint la famille plus large dont le nom figure dans le sien
 *     (wonder-lobby -> Lobby) ;
 *   - les autres serveurs seuls partagent une section commune (« Serveurs »).
 * Sur NationsGlory, où chaque serveur porte un nom unique suffixé par son groupe, la page tient ainsi
 * en deux sections au lieu d'une par serveur ; sur Paladium, lobby-N et faction-N restent groupés.
 */

/** Séparateurs admis dans un nom de serveur (lobby-1, lobby_1, lobby 1). */
const SEP = '[-_\\s]';

// Préfixes d'infrastructure à masquer (non-jeu) : page Server et Dashboard.
const INFRA_PREFIXES = ['proxy', 'build', 'cdn', 'warzone', 'beta'];

/** Infra (proxy, build, cdn...) : lu sur le début du nom, avant tout regroupement. */
export function isInfraServer(name: string): boolean {
  const n = name.toLowerCase().trim();
  return INFRA_PREFIXES.some((p) => n.startsWith(p));
}

/** Clé de la section commune des serveurs seuls de leur famille. */
export const STANDALONE_FAMILY = '__standalone';

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Suffixes de groupe à retirer des noms : clés et libellés du catalogue, normalisés (minuscules,
 * espaces en tirets), sans doublon, les plus longs d'abord (un groupe « iris-2 » passe avant « iris »).
 */
export function groupSuffixes(values: Iterable<string | null | undefined>): string[] {
  const out = new Set<string>();
  for (const v of values) {
    const s = (v ?? '').trim().toLowerCase().replace(/\s+/g, '-');
    if (s) out.add(s);
  }
  return [...out].sort((a, b) => b.length - a.length);
}

/**
 * Nom sans son suffixe de groupe, casse d'origine gardée : earth-iris -> earth, lobby-iris-2 ->
 * lobby-2, lobby-2-iris -> lobby-2. Un nom réduit au seul groupe (« iris ») reste tel quel.
 *
 * @param suffixes sortie de groupSuffixes
 * @param serverGroup groupe du serveur (RECUBE_GROUP), essayé en premier
 */
export function stripGroupSuffix(name: string, suffixes: readonly string[], serverGroup?: string | null): string {
  const trimmed = name.trim();
  const own = groupSuffixes([serverGroup]);
  for (const key of [...own, ...suffixes]) {
    const re = new RegExp(`^(.*?[^-_\\s])${SEP}+${escapeRegExp(key)}(${SEP}*\\d+)?$`, 'i');
    const m = trimmed.match(re);
    if (m) return m[1] + (m[2] ?? '');
  }
  return trimmed;
}

/** Famille d'un nom déjà sans groupe : minuscules, sans numéro final (mining-7 -> mining). */
export function familyOf(baseName: string): string {
  const base = baseName.toLowerCase().trim().replace(/[-_\s]*\d+$/, '');
  return base === 'minage' ? 'mining' : base;
}

/** Libellé lisible : séparateurs en espaces, initiale en capitale, sigles courts en capitales (rp -> RP). */
export function prettyName(raw: string): string {
  const words = raw.trim().split(/[-_\s]+/).filter(Boolean);
  if (words.length === 0) return raw;
  const text = words
    .map((w) => (/^[a-z]{1,2}$/i.test(w) ? w.toUpperCase() : w))
    .join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Tri naturel (lobby-2 avant lobby-10). */
export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, 'fr', { numeric: true, sensitivity: 'base' });
}

export interface FamilySection<T> {
  /** Clé de famille, ou STANDALONE_FAMILY pour la section commune. */
  key: string;
  items: T[];
}

/**
 * Sections de la page : familles de 2 serveurs ou plus (avec les serveurs seuls qui s'y rattachent
 * par leur nom), puis la section commune si des serveurs restent seuls. Ordre des sections et des
 * serveurs laissé à l'appelant.
 *
 * À calculer sur la liste complète, pas sur la liste filtrée : un filtre ne doit pas déplacer un
 * serveur d'une section à l'autre.
 */
export function groupFamilies<T>(items: readonly T[], familyKeyOf: (item: T) => string): FamilySection<T>[] {
  const byKey = new Map<string, T[]>();
  for (const item of items) {
    const k = familyKeyOf(item);
    const list = byKey.get(k);
    if (list) list.push(item);
    else byKey.set(k, [item]);
  }

  // Familles larges (2 serveurs ou plus, nom non vide), les plus longues d'abord : wonder-lobby-pvp
  // rejoint « lobby-pvp » avant « lobby ».
  const sections = new Map<string, T[]>(
    [...byKey]
      .filter(([k, list]) => k !== '' && list.length > 1)
      .sort(([a], [b]) => b.length - a.length)
      .map(([k, list]): [string, T[]] => [k, [...list]]),
  );
  const large = [...sections.keys()];
  const standalone: T[] = [];

  for (const [key, list] of byKey) {
    if (sections.has(key)) continue;
    const parent = key === ''
      ? undefined
      : large.find((l) => new RegExp(`(^|${SEP})${escapeRegExp(l)}(${SEP}|$)`).test(key));
    const target = parent === undefined ? undefined : sections.get(parent);
    if (target) target.push(...list);
    else standalone.push(...list);
  }

  const out: FamilySection<T>[] = [...sections].map(([key, list]) => ({ key, items: list }));
  if (standalone.length > 0) out.push({ key: STANDALONE_FAMILY, items: standalone });
  return out;
}

/**
 * Joueurs en ligne de chaque serveur : { identifiant : indices des joueurs dans `players` }.
 *
 * Le serveur d'un joueur (nom vu par le proxy) se rapproche du nom Pterodactyl comme avant
 * (égalité, ou l'un contient l'autre : « earth » pour earth-iris), à deux règles près :
 *   - un nom égal l'emporte : un joueur de lobby-10 ne compte plus aussi sur lobby-1 ;
 *   - un joueur sans serveur connu ne compte nulle part ('x'.includes('') serait vrai partout).
 * Les indices permettent de compter une section sans compter deux fois le même joueur.
 */
export function playersByServer(
  servers: readonly { identifier: string; name: string }[],
  players: readonly { serverName?: string | null }[],
): Map<string, Set<number>> {
  const out = new Map<string, Set<number>>();
  const named = servers.map((s) => ({ id: s.identifier, name: s.name.trim().toLowerCase() }));
  for (const s of named) out.set(s.id, new Set());

  players.forEach((p, index) => {
    const playerServer = (p.serverName ?? '').trim().toLowerCase();
    if (!playerServer) return;
    const exact = named.filter((s) => s.name === playerServer);
    const targets = exact.length > 0
      ? exact
      : named.filter((s) => s.name !== '' && (playerServer.includes(s.name) || s.name.includes(playerServer)));
    for (const s of targets) out.get(s.id)?.add(index);
  });
  return out;
}
